/**
 * Core-100 R4 — CORE-AE GAP-001 / CORE-AD GAP-001: provenance survives composition.
 *
 * The Ticket Command Center and the Dayboard card compose figures of different evidence classes side by side —
 * a MEASURED elapsed time next to a DERIVED OptiFinish. The reader must be able to tell them apart.
 *
 * Same rules as the Passport registry (`src/lib/passport/factProvenance.ts`), whose SP-05 vocabulary and approved
 * copy are reused unchanged:
 *   - a component never hands a badge a class; it names a fact group and this registry, owned by the authority that
 *     produces the fact, decides the class;
 *   - a class without approved copy renders NOTHING (never the nearest approved phrase). OD-35 (2026-10-15) gave
 *     measurement its copy, so the elapsed time now reads "Recorded Measurement" beside the OptiFinish's
 *     "Calculated by OptiBay".
 */
import {
  presentEvidenceClass,
  type FactProvenanceRule,
  type ProvenancePresentation,
} from '@/lib/passport/factProvenance';

export const TICKET_FACT_PROVENANCE: Readonly<Record<string, FactProvenanceRule>> = {
  'ticket.optiFinish': {
    evidenceClass: 'derivation',
    authority:
      'CORE-AC: OptiFinish is derived from the booked time, the performing technician\'s efficiency and the ' +
      'time-affecting states; it is never measured (CORE-AC "a pure, reproducible derivation").',
  },
  // Core-100 R4 W3 (CORE-AD F: "a manual override recorded as a human decision"; CORE-AJ: "every pause carries a
  // reason"): why the work is on hold is what a PERSON said — the technician's answer to "Why are you stopping work?"
  // (pause_my_work → ticket_pause_sessions.pause_detail / paused_by_user_id), or a pre-Stage-D reason a person chose.
  // OptiBay never infers it, so it is an assertion, never shown as a machine fact.
  'ticket.pauseReason': {
    evidenceClass: 'assertion',
    authority:
      'CORE-AJ: a pause reason is the technician’s stated answer (pause_my_work records who paused and why); ' +
      'CORE-AD: a hold shown on the Dayboard is a human assertion, not something OptiBay measured or derived.',
  },
  'ticket.elapsed': {
    evidenceClass: 'measurement',
    authority:
      'CORE-AJ: elapsed work time is measured by the server timer (work episodes), not derived. OD-35 copy: ' +
      '"Recorded Measurement".',
  },
};

export function presentTicketFactProvenance(factGroupId: string): ProvenancePresentation | null {
  const rule = TICKET_FACT_PROVENANCE[factGroupId];
  if (!rule) return null;
  return presentEvidenceClass(rule.evidenceClass);
}
